import { all, call, takeLatest, put, take, race } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import {
  rejectPromiseAction,
  resolvePromiseAction
} from 'redux-saga-promise-actions'
import btoa from 'btoa'
import axios from 'axios'

import { roles } from '../../utils/constants'
import { showAlertMessage } from '../alert/actions'
import configureStore from '../configureStore'
import { getUserById } from '../users/services'

import {
  getSchoolConfig,
  getSchoolConfigSuccessFul,
  getSchoolTeacherByIdSuccessful,
  getSchoolContentCreatorsByIdSuccessful,
  getSchoolAdministratorByIdSuccessful,
  getSchoolCoordinatorByIdSuccessful,
  getSchoolParentByIdSuccessful,
  getSchoolContentCreatorsById,
  getSchoolAdministratorById,
  getSchoolTeacherById,
  clearCurrentSchool,
  getSchoolCoordinatorById,
  getSchoolParentById,
  getSchoolNurseById,
  getSchoolNurseByIdSuccess,
  getSchoolPsychologistById,
  getSchoolPsychologistByIdSuccess,
  getSchoolOmbudsmanById,
  getSchoolOmbudsmanByIdSuccess,
  getSchoolFinanceById,
  getSchoolFinanceByIdSuccess,
  getSchoolSecretaryById,
  getSchoolSecretaryByIdSuccess
} from '../school/actions'

import * as actions from './actions'
import * as services from './services'

function * procedureToGetSchools ({ currentOccupation, id }) {
  try {
    yield put(clearCurrentSchool())
    let requestAction, successType, response
    switch (currentOccupation[0]) {
      case roles.CONTENT_CREATOR:
        requestAction = getSchoolContentCreatorsById(id)
        successType = getSchoolContentCreatorsByIdSuccessful.type
        break
      case roles.SCHOOL_ADMIN:
        requestAction = getSchoolAdministratorById({ id })
        successType = getSchoolAdministratorByIdSuccessful.type
        break
      case roles.TEACHER:
        requestAction = getSchoolTeacherById({ id })
        successType = getSchoolTeacherByIdSuccessful.type
        break
      case roles.COORDINATOR:
        requestAction = getSchoolCoordinatorById({ id })
        successType = getSchoolCoordinatorByIdSuccessful.type
        break
      case roles.PARENT:
        requestAction = getSchoolParentById({ id })
        successType = getSchoolParentByIdSuccessful.type
        break
      case roles.NURSE:
        requestAction = getSchoolNurseById({ id })
        successType = getSchoolNurseByIdSuccess.type
        break
      case roles.PSYCHOLOGIST:
        requestAction = getSchoolPsychologistById({ id })
        successType = getSchoolPsychologistByIdSuccess.type
        break
      case roles.OMBUDSMAN:
        requestAction = getSchoolOmbudsmanById({ id })
        successType = getSchoolOmbudsmanByIdSuccess.type
        break
      case roles.FINANCE:
        requestAction = getSchoolFinanceById({ id })
        successType = getSchoolFinanceByIdSuccess.type
        break
      case roles.SECRETARY:
        requestAction = getSchoolSecretaryById({ id })
        successType = getSchoolSecretaryByIdSuccess.type
        break
      default:
        break
    }
    // dispatch action and take the internal success action response
    if (requestAction) {
      yield put(requestAction)
      const { success } = yield race({
        success: take(action => action.type === successType),
        timeout: delay(10000)
      })
      if (success) {
        response = success?.payload?.response?.[0]
        if (response?.school_id) {
          yield put(getSchoolConfig(response.school_id))

          const { configSuccess } = yield race({
            configSuccess: take(getSchoolConfigSuccessFul.type),
            configTimeout: delay(10000)
          })

          if (!configSuccess) {
            throw new Error('Timeout getting school config')
          }
        }
      } else {
        throw new Error('Invalid data to get school config')
      }
    }
  } catch (err) {
    console.error(err)
    throw err
  }
}

export function * signIn ({ payload }) {
  const { history, ...rest } = payload
  try {
    const userData = yield call(services.signIn, rest)
    const userId = userData?.data?.data?.id
    const currentOccupation = [
      userData?.data?.data?.attributes?.occupations?.length > 1
        ? userData?.data?.data?.attributes?.occupations?.find(
            occup => occup !== 'parent'
          )
        : userData?.data?.data?.attributes?.occupations?.[0]
    ]
    yield put(
      actions.SIGN_IN_SUCCESSFUL({
        userData: userData,
        currentOccupation: currentOccupation.filter(item => item)
      })
    )
    const userSerialized = yield call(getUserById, { id: userId })
    yield put(
      actions.updateUserFromSignin({
        ...userData.data,
        data: {
          ...userData.data.data,
          attributes: {
            ...userData.data.data.attributes,
            can_manage_rooms: userSerialized.can_manage_rooms
          }
        }
      })
    )
    yield call(procedureToGetSchools, {
      currentOccupation: currentOccupation,
      id: userData?.data?.data?.id
    })
    yield put(actions.SIGN_IN_FINISHED())
  } catch (error) {
    yield put(actions.SIGN_IN_FAILURE(error))
    yield put(
      showAlertMessage({
        message: 'Usuário e/ou senha incorretos.',
        severity: 'error'
      })
    )
    if (error?.response?.status === 403 && rest.token) {
      const instance = axios.create({
        baseURL: process.env.REACT_APP_PROJECT_SSO_API_HOST,
        headers: {
          'X-Token': rest.token,
          'Content-Type': 'application/vnd.api+json'
        }
      })
      instance.post('/api/v1/users/logout')
    }
    yield call(signOut)
    history.push('/')
  }
}

export function * signOut () {
  try {
    yield call(services.signOut)
    yield put({ type: 'app:RESET_STORE' })
    yield put(actions.LOGOUT_SUCCESSFUL())
  } catch (error) {
    console.error(error)
    yield put({ type: 'app:RESET_STORE' })
    yield put(actions.LOGOUT_FAILURE())
  } finally {
    const { persistor } = configureStore()
    persistor.pause()
    persistor.flush().then(() => {
      return persistor.purge()
    })
  }
}

export function * switchUserRequest ({ payload }) {
  try {
    // redirect to default route before switch occupation and update the role sended in the platform-info
    payload.history.push('/')
    yield call(procedureToGetSchools, {
      id: payload.id,
      currentOccupation: [payload.occupation]
    })
    yield put(actions.switchUserSuccessful([payload.occupation]))
    // force a new render to distach each action with the new occupation value
    setTimeout(() => {
      payload.history.push('/')
    }, 1000)
  } catch (error) {
    console.error(error)
  }
}

export function * confirmPasswordRequest (action) {
  try {
    const { payload: { email, password } } = action
    const response = yield call(services.confirmPassword, {
      email,
      password: btoa(password)
    })
    resolvePromiseAction(action, response)
  } catch (error) {
    rejectPromiseAction(action, error)
  }
}

// Watchers
export function * watchSignIn () {
  yield takeLatest(actions.SIGN_IN, signIn)
}

export function * watchSignout () {
  yield takeLatest(actions.LOGOUT, signOut)
}

export function * watchSwitchOccupation () {
  yield takeLatest(actions.switchUser, switchUserRequest)
}

export function * confirmPassword () {
  yield takeLatest(actions.confirmPassword.request, confirmPasswordRequest)
}

export default function * authSagas () {
  yield all([
    watchSignIn(),
    watchSignout(),
    watchSwitchOccupation(),
    confirmPassword()
  ])
}
